import { useState, useEffect, useMemo } from "react";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { useProjectStore } from "@store/projectStore";
import { projectsApi } from "@services/api";
import { Project, ProjectBillingStep } from "types";
import { navigateTo } from "context/navigation.service";
import Icon1 from "@assets/icons/Icon";
import DataGrid from "@components/ui/tables/DataGrid";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import { createPortal } from "react-dom";
import { useRef } from "react";
import api from "@services/api/client";
import HelpTooltip from "@components/ui/HelpTooltip";

type MonthlyStats = {
  month: string;
  photos: number;
  validations: number;
  emails: number;
};

// --- Retard StepCell ---

function RetardCell({
  step,
  daysUntilEnd,
  kind,
  daysSincePrev,
  templateAbbreviation,
  customLabel,
}: {
  step: ProjectBillingStep;
  daysUntilEnd?: number | null;
  kind?: "late_payment" | "project_delay" | "step_gap";
  daysSincePrev?: number;
  /** Abbreviation looked up from the billing template by step index, used
   * as a fallback for projects predating the per-step abbreviation
   * snapshot on `project.billing.steps`. */
  templateAbbreviation?: string;
  /** When set, overrides every other label source — used to display a
   * single "Démarrage" pill that groups all is_demarrage retards. */
  customLabel?: string;
}) {
  const sentDate = step.sent_at
    ? new Date(step.sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })
    : "";
  // Prefer the explicit `customLabel` (used for grouped pills like "Démarrage").
  // Otherwise use the abbreviation snapshot, then template, then full label.
  const stepLabel =
    customLabel || step.abbreviation || templateAbbreviation || step.label || "Étape";

  if (kind === "late_payment" && step.sent_at) {
    const delay = step.payment_delay_days ?? 15;
    const daysLate = Math.floor((Date.now() - new Date(step.sent_at).getTime()) / (1000 * 60 * 60 * 24)) - delay;
    return (
      <span
        title={step.label || ""}
        className="inline-block px-2 py-0.5 rounded text-h5 bg-red-100 text-red-700"
      >
        {stepLabel} +{daysLate}j · {sentDate}
      </span>
    );
  }

  if (kind === "project_delay" && daysUntilEnd != null) {
    return (
      <span
        title={step.label || ""}
        className="inline-block px-2 py-0.5 rounded text-h5 bg-red-100 text-red-700"
      >
        {stepLabel} · non facturé · J{daysUntilEnd >= 0 ? "-" : "+"}
        {Math.abs(daysUntilEnd)}
      </span>
    );
  }

  if (kind === "step_gap" && step.step_gap_delay_days) {
    const days = Math.floor((daysSincePrev ?? 0) - step.step_gap_delay_days);
    return (
      <span
        title={step.label || ""}
        className="inline-block px-2 py-0.5 rounded text-h5 bg-amber-100 text-amber-700"
      >
        {stepLabel} · étape suivante +{days}j
      </span>
    );
  }

  return null;
}

// --- Main page ---

function ActivityChart({
  data: rawData,
  statsPeriod,
  setStatsPeriod,
  monthLabel,
}: {
  data: MonthlyStats[];
  statsPeriod: number;
  setStatsPeriod: (p: number) => void;
  monthLabel: (m: string) => string;
}) {
  const { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } = require("recharts");

  const data = [...rawData].reverse().map((s) => ({
    ...s,
    label: monthLabel(s.month),
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload) return null;
    return (
      <div className="bg-neutral-800 text-white rounded-lg px-3 py-2 text-h5 shadow-lg">
        <div className="font-medium mb-1">{label}</div>
        {payload.map((p: any, i: number) => (
          <div key={i} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: p.color }} />
            {p.value} {p.name.toLowerCase()}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="mt-4 max-w-1p2k bg-white rounded-xl border shadow-lg p-4">
      <div className="flex flex-row items-center justify-between mb-2">
        <div className="text-h3 font-medium text-neutral-700">Activité mensuelle</div>
        <div className="flex flex-row gap-1">
          {[3, 6, 12].map((p) => (
            <button
              key={p}
              className={`px-2.5 py-1 rounded-lg text-h5 border transition-colors ${
                statsPeriod === p
                  ? "bg-neutral-800 text-white border-neutral-800"
                  : "bg-white text-neutral-500 border-neutral-200 hover:border-neutral-400"
              }`}
              onClick={() => setStatsPeriod(p)}
            >
              {p} mois
            </button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} barGap={2} barCategoryGap="20%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} width={30} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f9fafb" }} />
          <Bar dataKey="photos" name="Photos" fill="#60a5fa" radius={[3, 3, 0, 0]} />
          <Bar dataKey="validations" name="Validations" fill="#34d399" radius={[3, 3, 0, 0]} />
          <Bar dataKey="emails" name="Emails" fill="#a78bfa" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>

      <div className="flex flex-row gap-5 mt-1 justify-center text-h4 text-neutral-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-400" />Photos</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-400" />Validations</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-violet-400" />Emails</span>
      </div>
    </div>
  );
}

export default function SuiviSalariesScreen() {
  const userData = useDataStore((state) => state.userData);
  const orgData = useDataStore((state) => state.orgData);
  const orgMembers = useDataStore((state) => state.orgMembers);
  const projectTemplates = useProjectStore((state) => state.projectTemplates);
  const fetchProjectTemplates = useProjectStore((state) => state.fetchProjectTemplates);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterConductorId, setFilterConductorId] = useState<string>("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [statsPeriod, setStatsPeriod] = useState<number>(6);

  // Indicateurs
  type Indicateur = {
    id: string;
    name: string;
    type: string;
    filters: { field: string; value: string }[];
    sum_field?: string;
    ratio_numerator?: string;
    ratio_denominator?: string;
    icon: string;
    color: string;
    value?: number;
  };
  const [indicateurs, setIndicateurs] = useState<Indicateur[]>([]);
  const [showIndicateurModal, setShowIndicateurModal] = useState(false);
  const [billingTemplates, setBillingTemplates] = useState<
    {
      id: string;
      name: string;
      steps: { label: string; abbreviation?: string; is_demarrage?: boolean }[];
    }[]
  >([]);
  const filterBtnRef = useRef<HTMLButtonElement>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  // Strict: the "Tous les conducteurs" dropdown should only list real
  // conducteurs. Owners/admins, commerciaux and viewers no longer appear
  // here even if they happen to be assigned to a project's `conductor_id`
  // slot — the row's conductor name still resolves correctly through the
  // full org member list below.
  const conductors = orgMembers.filter(
    (m) => m.status !== "inactive" && m.role === "conductor",
  );

  useEffect(() => {
    if (!filterOpen) return;
    const handler = (e: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node) &&
          filterBtnRef.current && !filterBtnRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [filterOpen]);

  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);

  // Ensure project templates are loaded
  useEffect(() => {
    if (userData?.org_id && projectTemplates.length === 0) {
      fetchProjectTemplates(userData.org_id);
    }
  }, [userData?.org_id]);

  // Fetch projects with billing
  useEffect(() => {
    if (!userData?.org_id) return;
    setIsLoading(true);
    projectsApi.list({
      org_id: userData.org_id,
      conductor_id: filterConductorId || undefined,
      // Server-side filter: only fetch projects with at least one billing
      // retard (single Firestore `==` on the derived has_any_retard flag).
      // No need to over-fetch and filter client-side anymore.
      with_billing_retards: true,
      limit: 200,
    }).then((data) => {
      setProjects(data);
    }).catch(() => {}).finally(() => setIsLoading(false));
  }, [userData?.org_id, filterConductorId]);

  const [loadingStats, setLoadingStats] = useState(false);

  // Fetch activity stats from backend
  useEffect(() => {
    if (!userData?.org_id) return;
    setLoadingStats(true);
    const params: any = { months: statsPeriod };
    if (filterConductorId) params.conductor_id = filterConductorId;
    api.get("/stats/activity", { params }).then(({ data }) => {
      setMonthlyStats(data.monthly || []);
    }).catch(() => {}).finally(() => setLoadingStats(false));
  }, [userData?.org_id, filterConductorId, statsPeriod]);

  // Fetch indicateurs
  const fetchIndicateurs = () => {
    const params: any = {};
    if (filterConductorId) params.conductor_id = filterConductorId;
    api.get("/stats/indicateurs", { params }).then(({ data }) => {
      setIndicateurs(data || []);
    }).catch(() => {});
  };

  useEffect(() => {
    fetchIndicateurs();
    // Fetch billing templates for the indicateur modal
    if (userData?.org_id) {
      api.get("/billing-templates", { params: { org_id: userData.org_id } }).then(({ data }) => {
        // Keep `steps` so the retard rollup can read the per-step
        // abbreviation set by the user in Paramètres → Modèles de
        // facturation. Existing projects predating the abbreviation
        // snapshot on `project.billing.steps` rely on this lookup.
        setBillingTemplates(
          data.map((bt: any) => ({
            id: bt.id,
            name: bt.name,
            steps: (bt.steps || []).map((s: any) => ({
              label: s.label,
              abbreviation: s.abbreviation || undefined,
              is_demarrage: !!s.is_demarrage,
            })),
          })),
        );
      }).catch(() => {});
    }
  }, [userData?.org_id, filterConductorId]);

  // Projects with billing retards — uses pre-computed billing_flags
  // (backend recomputes on billing changes and in daily cron). Mirrors
  // BillingTab's red-dot rule: payment delay, project delay, or step gap.
  const retardProjects = useMemo(() => {
    return projects.filter((p) => {
      const flags = p.billing?.billing_flags;
      return (
        !!flags &&
        (flags.has_late || flags.has_project_delay || flags.has_step_gap)
      );
    });
  }, [projects]);

  // Current month stats
  const currentMonth = new Date().toISOString().slice(0, 7);
  const currentStats = monthlyStats.find((s) => s.month === currentMonth) || { photos: 0, validations: 0, emails: 0 };

  // Retard DataGrid
  const retardTable = useMemo(() => {
    const columns: any[] = [
      {
        field: "name",
        headerName: "Projet",
        flex: 1,
        renderCell: ({ row }: any) => (
          <div className="flex flex-col cursor-pointer" onClick={(e) => { e.stopPropagation(); navigateTo({ route: `/project/${row.id}?tab=facturation` }); }}>
            <span className="text-h3 font-medium text-neutral-800 hover:underline">{row.name}</span>
            <span className="text-h4 text-neutral-400">{row.conductor}</span>
          </div>
        ),
      },
      {
        field: "retards",
        headerName: "Retards",
        flex: 2,
        renderCell: ({ row }: any) => (
          <div className="flex flex-wrap gap-1">
            {row.retards.map((r: any, i: number) => (
              <RetardCell
                key={i}
                step={r.step}
                daysUntilEnd={r.daysUntilEnd}
                kind={r.kind}
                daysSincePrev={r.daysSincePrev}
                templateAbbreviation={r.templateAbbreviation}
                customLabel={r.customLabel}
              />
            ))}
          </div>
        ),
      },
      {
        field: "total",
        headerName: "Total TTC",
        width: 110,
        renderCell: ({ row }: any) => (
          <span className="text-h3 font-medium text-neutral-700">{row.total}</span>
        ),
      },
    ];

    const rows = retardProjects.map((project) => {
      const billing = project.billing!;
      const endDateStr = billing.planning_end_date;
      const daysUntilEnd = endDateStr ? Math.round((new Date(endDateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
      const conductorId = project.infos?.team?.conductor_id;
      // Resolve from the full member list so admins/commerciaux that are
      // assigned to the conductor slot still display a name, even though
      // they're filtered out of the dropdown above.
      const conductor = conductorId ? orgMembers.find((c) => c.id === conductorId) : null;

      // Resolve the per-step abbreviation from the billing template by
      // matching labels (most reliable across edits) and falling back to
      // index when labels diverge.
      const tpl = billingTemplates.find((t) => t.id === billing.billing_template_id);
      const abbrFor = (step: ProjectBillingStep, idx: number): string | undefined => {
        if (!tpl) return undefined;
        const byLabel = tpl.steps.find((s) => s.label === step.label);
        if (byLabel?.abbreviation) return byLabel.abbreviation;
        return tpl.steps[idx]?.abbreviation;
      };

      // Match the backend's datetime comparison exactly — no Math.floor on
      // day diffs, otherwise the boundary hours after the due date are
      // missed (back says "overdue", front says "not yet"). Use ms to be
      // safe across timezones and DST.
      const DAY_MS = 24 * 60 * 60 * 1000;
      const nowMs = Date.now();

      const rawRetards = billing.steps
        .map((step, idx) => {
          const templateAbbreviation = abbrFor(step, idx);
          // Late payment — compare due timestamp, not floored days.
          if (step.status === "sent" && step.sent_at) {
            const sentMs = new Date(step.sent_at).getTime();
            const delay = step.payment_delay_days ?? 15;
            const dueMs = sentMs + delay * DAY_MS;
            if (nowMs > dueMs)
              return { step, daysUntilEnd, kind: "late_payment" as const, templateAbbreviation };
          }
          // Project delay
          if (
            step.status === "pending" &&
            step.project_delay_days &&
            daysUntilEnd != null &&
            daysUntilEnd <= step.project_delay_days
          ) {
            return { step, daysUntilEnd, kind: "project_delay" as const, templateAbbreviation };
          }
          // Step gap — same logic as BillingTab.tsx, also datetime-based.
          if (
            step.step_gap_delay_days &&
            ["pending", "draft", "finalized"].includes(step.status) &&
            !step.is_demarrage
          ) {
            for (let i = idx - 1; i >= 0; i--) {
              const prev = billing.steps[i];
              if (prev.sent_at && (prev.status === "sent" || prev.status === "paid")) {
                const prevSentMs = new Date(prev.sent_at).getTime();
                const gapMs = step.step_gap_delay_days * DAY_MS;
                if (nowMs - prevSentMs > gapMs) {
                  return {
                    step,
                    daysUntilEnd,
                    kind: "step_gap" as const,
                    daysSincePrev: (nowMs - prevSentMs) / DAY_MS,
                    templateAbbreviation,
                  };
                }
                break;
              }
            }
          }
          return null;
        })
        .filter((r): r is NonNullable<typeof r> => r !== null);

      // Group every `is_demarrage` retard into a single "Démarrage" pill.
      // We pick the demarrage step with the OLDEST sent_at (the most
      // overdue) as the representative, then tag it with `customLabel`
      // so RetardCell shows "Démarrage" instead of the per-step abbrev.
      const demarrageRetards = rawRetards.filter((r) => r.step.is_demarrage);
      const otherRetards = rawRetards.filter((r) => !r.step.is_demarrage);

      let groupedDemarrage: typeof rawRetards[number] | null = null;
      if (demarrageRetards.length > 0) {
        const oldest = demarrageRetards.reduce((acc, r) => {
          const aMs = acc.step.sent_at ? new Date(acc.step.sent_at).getTime() : Infinity;
          const rMs = r.step.sent_at ? new Date(r.step.sent_at).getTime() : Infinity;
          return rMs < aMs ? r : acc;
        });
        groupedDemarrage = { ...oldest, customLabel: "Démarrage" } as any;
      }

      const retards = groupedDemarrage
        ? [groupedDemarrage, ...otherRetards]
        : otherRetards;

      return {
        id: project.id,
        name: project.infos?.general?.name || project.id,
        conductor: conductor?.infos?.display_name || "—",
        retards,
        total: `${Math.round(billing.montant_total * 1.2).toLocaleString("fr-FR")} €`,
      };
    });

    return { columns, rows };
  }, [retardProjects, orgMembers, billingTemplates]);

  const monthLabel = (m: string) => {
    const [y, mo] = m.split("-");
    const months = ["Janv", "Févr", "Mars", "Avr", "Mai", "Juin", "Juil", "Août", "Sept", "Oct", "Nov", "Déc"];
    return `${months[parseInt(mo) - 1]} ${y}`;
  };

  return (
    <div className="min-h-screen flex flex-col overflow-auto">
      <div className="flex-shrink-0 pt-10 px-4 lg:px-10 pb-4">
        <div className="flex flex-col w-full max-w-1p2k">
          <div className="flex flex-col mb-4">
            <Text1>Suivi des salariés</Text1>
            <Text2>Activité et retards de facturation par conducteur.</Text2>
          </div>

          {/* Conductor filter */}
          <div className="flex flex-row gap-2 items-center">
            <button
              ref={filterBtnRef}
              onClick={() => setFilterOpen(!filterOpen)}
              className={`inline-flex items-center gap-1 px-3 py-2 border rounded-lg text-h4 ${
                filterConductorId
                  ? "border-neutral-800 text-neutral-800 bg-neutral-100"
                  : "border-neutral-300 text-neutral-500 hover:bg-neutral-50"
              }`}
            >
              <Icon1 name="expert" size={14} />
              {filterConductorId
                ? conductors.find((c) => c.id === filterConductorId)?.infos?.display_name || "Conducteur"
                : "Tous les conducteurs"}
              {filterConductorId && (
                <span
                  className="ml-1 text-h5 text-neutral-400 hover:text-neutral-600"
                  onClick={(e) => { e.stopPropagation(); setFilterConductorId(""); }}
                >
                  ✕
                </span>
              )}
            </button>
            {filterOpen && createPortal(
              <div
                ref={filterDropdownRef}
                style={{
                  position: "fixed",
                  top: (filterBtnRef.current?.getBoundingClientRect().bottom || 0) + 4,
                  left: filterBtnRef.current?.getBoundingClientRect().left || 0,
                }}
                className="z-50 bg-white border rounded-lg shadow-lg p-2 min-w-200 max-h-300 overflow-auto"
              >
                <div
                  className={`px-3 py-1.5 rounded text-h4 cursor-pointer ${!filterConductorId ? "bg-neutral-100 font-medium" : "hover:bg-neutral-50"}`}
                  onClick={() => { setFilterConductorId(""); setFilterOpen(false); }}
                >
                  Tous les conducteurs
                </div>
                {conductors.map((c) => (
                  <div
                    key={c.id}
                    className={`px-3 py-1.5 rounded text-h4 cursor-pointer ${filterConductorId === c.id ? "bg-neutral-100 font-medium" : "hover:bg-neutral-50"}`}
                    onClick={() => { setFilterConductorId(c.id); setFilterOpen(false); }}
                  >
                    {c.infos?.display_name || c.infos?.email || c.id}
                  </div>
                ))}
              </div>,
              document.body,
            )}
          </div>
        </div>
      </div>

      {/* Stats cards */}
      <div className="flex-shrink-0 px-4 lg:px-10 pb-4">
        <div className="flex flex-row gap-3 max-w-1p2k flex-wrap">
          {[
            { label: "Photos ce mois", value: currentStats.photos, icon: "photo", color: "#3b82f6" },
            { label: "Étapes validées ce mois", value: currentStats.validations, icon: "done", color: "#10b981" },
            { label: "Emails envoyés ce mois", value: currentStats.emails, icon: "email", color: "#8b5cf6" },
            { label: "Projets en retard", value: retardProjects.length, icon: "warning", color: "#ef4444" },
          ].map((card) => (
            <div
              key={card.label}
              className="flex flex-col gap-1 bg-white border rounded-xl p-4 flex-1 min-w-140 shadow-lg relative overflow-hidden"
            >
              {(loadingStats || isLoading) && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5">
                  <div
                    className="absolute h-full bg-blue-400 rounded-full"
                    style={{ animation: "statbar 1s ease-in-out infinite" }}
                  />
                </div>
              )}
              <div className="flex flex-row items-center justify-between">
                <div className="text-h3 text-neutral-400">{card.label}</div>
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: card.color + "20" }}
                >
                  <Icon1 name={card.icon} size={18} color={card.color} />
                </div>
              </div>
              <div className="text-2xl font-semibold text-black">{card.value}</div>
            </div>
          ))}

          {/* Custom indicateurs inline */}
          {indicateurs.map((ind) => (
            <div
              key={ind.id}
              className="flex flex-col gap-1 bg-white border rounded-xl p-4 flex-1 min-w-140 shadow-lg relative overflow-hidden"
            >
              <div className="flex flex-row items-center justify-between">
                <div className="text-h3 text-neutral-400">{ind.name}</div>
                <div className="flex items-center gap-1">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: ind.color + "20" }}
                  >
                    <Icon1 name={ind.icon} size={18} color={ind.color} />
                  </div>
                  {isOwnerOrAdmin(userData) && (
                    <button
                      className="text-h5 text-neutral-300 hover:text-red-500"
                      onClick={async () => {
                        await api.delete(`/stats/indicateurs/${ind.id}`);
                        fetchIndicateurs();
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="text-2xl font-semibold text-black">
                {(ind.value ?? 0).toLocaleString("fr-FR")}{(ind as any).unit === "€" ? " €" : (ind as any).unit === "%" ? "%" : (ind as any).unit ? ` ${(ind as any).unit}` : ""}
              </div>
            </div>
          ))}

          {/* Add indicateur button */}
          {isOwnerOrAdmin(userData) && (
            <button
              className="flex flex-col gap-1 border border-dashed border-neutral-300 rounded-xl p-4 min-w-140 items-center justify-center text-neutral-400 hover:border-neutral-400 hover:text-neutral-500 transition-colors cursor-pointer"
              onClick={() => setShowIndicateurModal(true)}
            >
              <Icon1 name="plus" size={20} />
              <span className="text-h4">Indicateur</span>
            </button>
          )}
        </div>

        {/* Monthly bar chart */}
        {monthlyStats.length > 0 && (
          <ActivityChart data={monthlyStats} statsPeriod={statsPeriod} setStatsPeriod={setStatsPeriod} monthLabel={monthLabel} />
        )}
      </div>

      {/* Retard DataGrid */}
      <div className="px-4 lg:px-10 pb-6 flex flex-col w-full" style={{ minHeight: 400 }}>
        <div className="flex flex-row items-center gap-2 mb-2">
          <div className="text-h2 font-semibold text-neutral-700">Retards de paiement et de facturation</div>
          <HelpTooltip
            content={
              <div className="flex flex-col gap-1.5">
                <div>Cette liste regroupe les deux types de retards :</div>
                <div>
                  • <b>Retard de paiement</b> : une facture a été envoyée au
                  client mais n'est toujours pas payée au-delà du délai défini
                  (15 jours par défaut).
                </div>
                <div>
                  • <b>Retard de facturation</b> : la fin du planning du
                  projet approche, mais une étape n'a toujours pas été
                  facturée.
                </div>
              </div>
            }
          />
        </div>
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-12 rounded-lg bg-neutral-100 animate-pulse" />
            ))}
          </div>
        ) : retardTable.rows.length > 0 ? (
          <div className="max-w-1p2k w-full">
            <DataGrid table={retardTable} hide_lines={true} />
          </div>
        ) : (
          <div className="text-h3 text-neutral-400 py-8 text-center">
            Aucun retard détecté.
          </div>
        )}
      </div>

      {/* Indicateur creation modal */}
      {showIndicateurModal && (
        <IndicateurModal
          billingTemplates={billingTemplates}
          projectTemplates={projectTemplates}
          conductors={conductors}
          onClose={() => setShowIndicateurModal(false)}
          onCreated={() => { setShowIndicateurModal(false); fetchIndicateurs(); }}
        />
      )}
    </div>
  );
}

// --- Indicateur Modal ---

function IndicateurModal({
  billingTemplates,
  projectTemplates,
  conductors,
  onClose,
  onCreated,
}: {
  billingTemplates: { id: string; name: string }[];
  projectTemplates: any[];
  conductors: any[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [type, setType] = useState("count");
  const [filterField, setFilterField] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [filters, setFilters] = useState<{ field: string; value: string }[]>([]);
  const [metric, setMetric] = useState("montant_total");
  const [ratioNum, setRatioNum] = useState("invoiced_amount");
  const [ratioDen, setRatioDen] = useState("montant_total");
  const [icon, setIcon] = useState("chart");
  const [color, setColor] = useState("#3b82f6");

  const metricOptions = [
    { value: "project_count", label: "Nombre de projets" },
    { value: "montant_total", label: "Montant total HT" },
    { value: "invoiced_amount", label: "Montant facturé" },
    { value: "paid_amount", label: "Montant encaissé" },
    { value: "pending_amount", label: "Montant en attente" },
    { value: "billing_progress", label: "Avancement facturation (%)" },
    { value: "completion_pct", label: "Avancement chantier (%)" },
    { value: "duration_days", label: "Durée du projet (jours)" },
    { value: "steps_count", label: "Nombre d'étapes facture" },
    { value: "steps_completed", label: "Étapes facturées (payées)" },
    { value: "photos_count", label: "Nombre de photos" },
  ];
  const [saving, setSaving] = useState(false);

  const filterFieldOptions = [
    { value: "project_template_id", label: "Modèle de projet" },
    { value: "billing_template_id", label: "Modèle de facturation" },
    { value: "status", label: "Statut du projet" },
    { value: "conductor_id", label: "Conducteur" },
  ];

  const filterValueOptions = (() => {
    if (filterField === "project_template_id") return projectTemplates.map((pt) => ({
      value: pt.id, label: pt.infos?.general?.name || pt.id,
    }));
    if (filterField === "billing_template_id") return billingTemplates.map((bt) => ({ value: bt.id, label: bt.name }));
    if (filterField === "status") return [
      { value: "in_progress", label: "En cours" },
      { value: "not_started", label: "En attente" },
      { value: "delivered", label: "Livré" },
      { value: "completed", label: "Terminé" },
    ];
    if (filterField === "conductor_id") return conductors.map((c) => ({
      value: c.id, label: c.infos?.display_name || c.id,
    }));
    return [];
  })();

  const addFilter = () => {
    if (filterField && filterValue) {
      setFilters([...filters, { field: filterField, value: filterValue }]);
      setFilterField("");
      setFilterValue("");
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showNotification({ type: "error", message: "Le nom est obligatoire" });
      return;
    }
    setSaving(true);
    try {
      await api.post("/stats/indicateurs", {
        name,
        type,
        filters,
        metric: (type === "sum" || type === "average") ? metric : undefined,
        ratio_numerator: type === "ratio" ? ratioNum : undefined,
        ratio_denominator: type === "ratio" ? ratioDen : undefined,
        unit: type === "ratio" ? "%" : (metric?.includes("amount") || metric === "montant_total") ? "€" : "",
        icon,
        color,
      });
      showNotification({ type: "success", message: "Indicateur créé" });
      onCreated();
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la création" });
    } finally {
      setSaving(false);
    }
  };

  const getFilterLabel = (f: { field: string; value: string }) => {
    const fieldLabel = filterFieldOptions.find((o) => o.value === f.field)?.label || f.field;
    let valueLabel = f.value;
    if (f.field === "project_template_id") valueLabel = projectTemplates.find((pt) => pt.id === f.value)?.infos?.general?.name || f.value;
    if (f.field === "billing_template_id") valueLabel = billingTemplates.find((bt) => bt.id === f.value)?.name || f.value;
    if (f.field === "conductor_id") valueLabel = conductors.find((c) => c.id === f.value)?.infos?.display_name || f.value;
    if (f.field === "status") {
      const statusLabels: Record<string, string> = { in_progress: "En cours", not_started: "En attente", delivered: "Livré", completed: "Terminé", sav: "SAV" };
      valueLabel = statusLabels[f.value] || f.value;
    }
    return `${fieldLabel} = ${valueLabel}`;
  };

  const colors = ["#3b82f6", "#10b981", "#8b5cf6", "#f59e0b", "#ef4444", "#06b6d4", "#ec4899"];

  return (
    <div className="fixed inset-0 z-50 bg-shadow_10" onClick={onClose}>
      <div
        className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col animate-slide-in-right"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-row justify-between p-4 items-center border-b">
          <div className="text-h2 font-medium">Nouvel indicateur</div>
          <SecondaryButton label="Fermer" onClick={onClose} variant="small" />
        </div>
        <div className="flex-1 overflow-y-auto">
      <div className="flex flex-col gap-4 p-4">
        <TextField label="Nom" value={name} onChange={setName} placeholder="Ex: Projets RE2020" />

        <DropDown
          label="Type de calcul"
          category="Type"
          options={[
            { value: "count", label: "Nombre de projets" },
            { value: "sum", label: "Somme" },
            { value: "average", label: "Moyenne" },
            { value: "ratio", label: "Ratio (%)" },
          ]}
          value={type}
          onSelect={(v) => setType(v as string)}
        />

        {(type === "sum" || type === "average") && (
          <DropDown
            label={type === "sum" ? "Métrique à additionner" : "Métrique à moyenner"}
            category="Métrique"
            options={metricOptions}
            value={metric}
            onSelect={(v) => setMetric(v as string)}
          />
        )}

        {type === "ratio" && (
          <>
            <DropDown
              label="Numérateur"
              category="Numérateur"
              options={metricOptions}
              value={ratioNum}
              onSelect={(v) => setRatioNum(v as string)}
            />
            <DropDown
              label="Dénominateur"
              category="Dénominateur"
              options={metricOptions}
              value={ratioDen}
              onSelect={(v) => setRatioDen(v as string)}
            />
          </>
        )}

        {/* Filters */}
        <div className="border-t pt-3">
          <div className="text-h3 font-medium text-neutral-600 mb-1">Filtres</div>
          <div className="text-h5 text-neutral-400 mb-2">
            Même champ = OU (union) · Champs différents = ET (intersection)
          </div>
          {(() => {
            // Group filters by field for display
            const groups: Record<string, { field: string; value: string }[]> = {};
            filters.forEach((f) => {
              if (!groups[f.field]) groups[f.field] = [];
              groups[f.field].push(f);
            });
            const fieldKeys = Object.keys(groups);
            return fieldKeys.map((field, gi) => (
              <div key={field} className="mb-2">
                {gi > 0 && <div className="text-h5 font-semibold text-neutral-800 my-1 text-center">ET</div>}
                <div className="bg-neutral-50 rounded-lg px-3 py-2">
                  {groups[field].map((f, fi) => (
                    <div key={fi}>
                      {fi > 0 && <div className="text-h5 text-blue-500 font-medium my-0.5">OU</div>}
                      <div className="flex items-center gap-2">
                        <span className="text-h4 text-neutral-600 flex-1">{getFilterLabel(f)}</span>
                        <button className="text-h5 text-red-400 hover:text-red-600" onClick={() => setFilters(filters.filter((x) => !(x.field === f.field && x.value === f.value)))}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ));
          })()}
          <div className="flex flex-row gap-2 items-end mt-2">
            <div className="flex-1">
              <DropDown
                label="Champ"
                category="Filtre"
                options={[{ value: "", label: "Choisir..." }, ...filterFieldOptions]}
                value={filterField}
                onSelect={(v) => { setFilterField(v as string); setFilterValue(""); }}
              />
            </div>
            {filterField && (
              <div className="flex-1">
                <DropDown
                  label="Valeur"
                  category="Valeur"
                  options={[{ value: "", label: "Choisir..." }, ...filterValueOptions]}
                  value={filterValue}
                  onSelect={(v) => setFilterValue(v as string)}
                />
              </div>
            )}
            {filterField && filterValue && (
              <SecondaryButton variant="small" label="Ajouter" onClick={addFilter} />
            )}
          </div>
        </div>

        {/* Color */}
        <div className="border-t pt-3">
          <div className="text-h3 font-medium text-neutral-600 mb-2">Couleur</div>
          <div className="flex flex-row gap-2">
            {colors.map((c) => (
              <button
                key={c}
                className={`w-7 h-7 rounded-full border-2 ${color === c ? "border-neutral-800" : "border-transparent"}`}
                style={{ backgroundColor: c }}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-row justify-end gap-2 border-t pt-3">
          <SecondaryButton variant="small" label="Annuler" onClick={onClose} />
          <Button label={saving ? "..." : "Créer"} onClick={handleSave} disabled={saving} />
        </div>
      </div>
        </div>
      </div>
    </div>
  );
}
